import { environment } from 'environment';
import { ReferralNet } from 'networking';
import { Form, type ReferralTypes, type referralSchemas } from 'types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { devtools, getStorage } from './helpers';
import { Sockets, useSockets } from './sockets';
import { useUser } from './user';

interface PartnerReferralState {
  totals?: ReferralTypes['totals'];
  referrals?: ReferralTypes['referral'][];

  getReferral: (id: string) => ReferralTypes['referral'] | undefined;
  upsertReferral: (referral: ReferralTypes['referral']) => void;
  deleteReferral: (id: string) => void;

  setTotal: (total: ReferralTypes['totals']) => void;
  setReferrals: (referrals: ReferralTypes['referral'][]) => void;

  markActivitySeen: (activity: ReferralTypes['activity']) => Promise<void>;

  _fetching: string[];
  _fetched: { referrals: boolean };

  fetchReferrals: (page?: number) => Promise<ReferralTypes['referral'][]>;
  fetchReferral: (id: string) => Promise<ReferralTypes['referral'] | undefined>;

  _rehydrated: boolean;
  _setRehydrated: (_rehydrated: boolean) => void;

  form: Form<typeof referralSchemas.referral>;
  setForm: (form: React.SetStateAction<Form<typeof referralSchemas.referral>>, cleanSet?: boolean) => void;
  resetForm: (cleanSet?: boolean) => void;
  _formDirty: boolean;
  _setFormDirty: (dirty: boolean) => void;

  _socket: Sockets['referral'] | undefined;
  _getSocket: () => Promise<Sockets['referral']>;

  _reset: () => void;
}

export const usePartnerReferrals = create<PartnerReferralState>()(
  devtools(
    persist(
      (set, get) => ({
        totals: undefined,
        referrals: undefined,

        getReferral: (id: string) => {
          if (!id || id === 'new') return undefined;

          const referral = get().referrals?.find(referral => referral.id === id);
          if (referral) return referral;

          void get().fetchReferral(id);
          return referral;
        },
        upsertReferral: (referral: ReferralTypes['referral']) => {
          if (!referral) return;

          const referrals = get().referrals?.filter(_referral => _referral.id !== referral.id) || [];
          set(s => ({ ...s, referrals: [...referrals, referral] }));
        },
        deleteReferral: (id: string) => {
          if (!id) return;

          const referrals = get().referrals?.filter(referral => referral.id !== id) || [];
          set(s => ({ ...s, referrals }));
        },

        setTotal: (total: ReferralTypes['totals']) => set(s => ({ ...s, total })),
        setReferrals: (referrals: ReferralTypes['referral'][]) => set(s => ({ ...s, referrals })),

        markActivitySeen: async (activity: ReferralTypes['activity']) => {
          if (!activity || !activity.id) return;
          if (get()._fetching.includes(activity.id)) return;
          set(s => ({ ...s, _fetching: [...s._fetching, activity.id] }));

          const socket = await get()._getSocket();
          socket.emit('markActivitySeen', { activityId: activity.id });

          const referrals = get().referrals?.map(referral => {
            if (referral.id !== activity.referralId) return referral;
            return {
              ...referral,
              activities: referral.activities.map(_activity => {
                if (_activity.id !== activity.id) return _activity;
                return { ..._activity, seenBy: [..._activity.seenBy, { id: useUser.getState().user.id }] };
              }),
            };
          });

          set(s => ({ ...s, referrals, _fetching: s._fetching.filter(id => id !== activity.id) }));
        },

        _fetching: [],
        _fetched: { referrals: false satisfies boolean },

        fetchReferrals: async (page = 1) => {
          if (get()._fetching.includes('referrals')) return [];
          if (get()._fetched.referrals && get().referrals?.length >= get().totals?.TOTAL) return;

          set(s => ({ ...s, _fetching: [...s._fetching, 'referrals'] }));

          const res = await ReferralNet.general.getMy(page);
          if (res.success === false) return [];
          const { referrals, totals } = res.data;

          set(s => ({
            ...s,
            _fetching: s._fetching.filter(id => id !== 'referrals'),
            _fetched: { ...s._fetched, referrals: true },
            totals,
            referrals: [
              ...(s.referrals || []).filter(referral => !referrals.find(_referral => _referral.id === referral.id)),
              ...referrals,
            ],
          }));

          return referrals;
        },
        fetchReferral: async (id: string) => {
          if (!id || id === 'new' || get()._fetching.includes(id)) return undefined;

          set(s => ({ ...s, _fetching: [...s._fetching, id] }));
          const res = await ReferralNet.general.get(id);
          if (res.success === false) return;
          const referral = res.data;

          set(s => ({ ...s, _fetching: s._fetching.filter(_id => _id !== id) }));

          get().upsertReferral(referral);
          if (!referral && typeof window !== 'undefined') window.location.replace('/referrals/');

          return referral;
        },

        _rehydrated: false satisfies boolean,
        _setRehydrated: (_rehydrated: boolean) => set(s => ({ ...s, _rehydrated })),

        form: { address: {}, theChildren: [{}], adults: [] },
        setForm: (
          value:
            | Form<typeof referralSchemas.referral>
            | ((prevState: Form<typeof referralSchemas.referral>) => Form<typeof referralSchemas.referral>),
          cleanSet?: boolean,
        ) => set(s => ({ ...s, form: typeof value === 'function' ? value(s.form) : value, _formDirty: !cleanSet })),
        resetForm: (cleanSet?: boolean) =>
          set(s => ({ ...s, form: { address: {}, theChildren: [{}], adults: [] }, _formDirty: !cleanSet })),
        _formDirty: false satisfies boolean,
        _setFormDirty: (_formDirty: boolean) => set(s => ({ ...s, _formDirty })),

        _socket: undefined,
        _getSocket: async () => {
          if (get()._socket?.connected) return get()._socket;
          const socket = await pollForConnectedReferralSocket();
          set(s => ({ ...s, _socket: socket }));
          return socket;
        },

        _reset: () => {
          usePartnerReferrals.persist.clearStorage();
          usePartnerReferrals.setState({
            totals: undefined,
            referrals: undefined,
            _rehydrated: true,
            _fetched: { referrals: false },
            _fetching: [],
            form: { theChildren: [{}], adults: [] },
            _formDirty: false,
            _socket: undefined,
          });
        },
      }),
      {
        name: `b4k-${environment.name}-referrals`,
        version: 1,
        onRehydrateStorage: () => state => state._setRehydrated(true),
        partialize: state =>
          Object.fromEntries(Object.entries(state).filter(([key]) => ['form', 'totals', 'referrals'].includes(key))),
        storage: getStorage(),
      },
    ),
    { name: 'referrals' },
  ),
);

export async function pollForConnectedReferralSocket() {
  return new Promise<Sockets['referral']>(resolve => {
    if (useSockets.getState()?.sockets?.referral?.connected) return resolve(useSockets.getState().sockets.referral);
    const interval = setInterval(() => {
      const socket = useSockets.getState().sockets.referral;
      if (socket?.connected) {
        clearInterval(interval);
        resolve(socket);
      }
    }, 100);
  });
}
